import { createContext, useContext } from 'react'
import {
	PermissionChecker,
	type UserPermissions,
	type PermissionContext,
} from './permissions-utils.ts'
import { type PermissionString } from './permissions.ts'

interface PermissionProviderValue {
	permissionChecker: PermissionChecker | null
	hasPermission: (
		permission: PermissionString,
		context?: PermissionContext,
	) => boolean
	hasAnyPermission: (
		permissions: PermissionString[],
		context?: PermissionContext,
	) => boolean
	hasAllPermissions: (
		permissions: PermissionString[],
		context?: PermissionContext,
	) => boolean
	canPerformAction: (
		entity: string,
		action: string,
		context?: PermissionContext,
	) => boolean
	getNightclubsWithPermission: (permission: PermissionString) => string[]
	getEventsWithPermission: (permission: PermissionString) => string[]
	// Event-specific helpers
	canManageEventTeam: (eventId?: string, nightclubId?: string) => boolean
	canInviteEventMembers: (eventId?: string, nightclubId?: string) => boolean
	canManageEventTickets: (eventId?: string, nightclubId?: string) => boolean
	canViewEventAnalytics: (eventId?: string, nightclubId?: string) => boolean
	canEditEvents: (eventId?: string, nightclubId?: string) => boolean
	canDeleteEvents: (eventId?: string, nightclubId?: string) => boolean
	canCreateEvents: (nightclubId?: string) => boolean
	isEventOrganizer: (eventId?: string, nightclubId?: string) => boolean
	isEventTicketManager: (eventId?: string, nightclubId?: string) => boolean
}

const PermissionContext = createContext<PermissionProviderValue | null>(null)

interface PermissionProviderProps {
	children: React.ReactNode
	permissions?: UserPermissions | null
}

export function PermissionProvider({
	children,
	permissions,
}: PermissionProviderProps) {
	const permissionChecker = permissions
		? new PermissionChecker(permissions)
		: null

	const hasPermission = (
		permission: PermissionString,
		context?: PermissionContext,
	): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.hasPermission(permission, context)
	}

	const hasAnyPermission = (
		permissions: PermissionString[],
		context?: PermissionContext,
	): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.hasAnyPermission(permissions, context)
	}

	const hasAllPermissions = (
		permissions: PermissionString[],
		context?: PermissionContext,
	): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.hasAllPermissions(permissions, context)
	}

	const canPerformAction = (
		entity: string,
		action: string,
		context?: PermissionContext,
	): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.canPerformAction(entity, action, context)
	}

	const getNightclubsWithPermission = (
		permission: PermissionString,
	): string[] => {
		if (!permissionChecker) return []
		return permissionChecker.getNightclubsWithPermission(permission)
	}

	const getEventsWithPermission = (permission: PermissionString): string[] => {
		if (!permissionChecker) return []
		return permissionChecker.getEventsWithPermission(permission)
	}

	// Event-specific helper functions
	const canManageEventTeam = (
		eventId?: string,
		nightclubId?: string,
	): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.canManageEventTeam(eventId, nightclubId)
	}

	const canInviteEventMembers = (
		eventId?: string,
		nightclubId?: string,
	): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.canInviteEventMembers(eventId, nightclubId)
	}

	const canManageEventTickets = (
		eventId?: string,
		nightclubId?: string,
	): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.canManageEventTickets(eventId, nightclubId)
	}

	const canViewEventAnalytics = (
		eventId?: string,
		nightclubId?: string,
	): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.canViewEventAnalytics(eventId, nightclubId)
	}

	const canEditEvents = (eventId?: string, nightclubId?: string): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.canEditEvents(eventId, nightclubId)
	}

	const canDeleteEvents = (eventId?: string, nightclubId?: string): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.canDeleteEvents(eventId, nightclubId)
	}

	const canCreateEvents = (nightclubId?: string): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.canCreateEvents(nightclubId)
	}

	const isEventOrganizer = (
		eventId?: string,
		nightclubId?: string,
	): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.isEventOrganizer(eventId, nightclubId)
	}

	const isEventTicketManager = (
		eventId?: string,
		nightclubId?: string,
	): boolean => {
		if (!permissionChecker) return false
		return permissionChecker.isEventTicketManager(eventId, nightclubId)
	}

	return (
		<PermissionContext.Provider
			value={{
				permissionChecker,
				hasPermission,
				hasAnyPermission,
				hasAllPermissions,
				canPerformAction,
				getNightclubsWithPermission,
				getEventsWithPermission,
				canManageEventTeam,
				canInviteEventMembers,
				canManageEventTickets,
				canViewEventAnalytics,
				canEditEvents,
				canDeleteEvents,
				canCreateEvents,
				isEventOrganizer,
				isEventTicketManager,
			}}
		>
			{children}
		</PermissionContext.Provider>
	)
}

export function usePermissions() {
	const context = useContext(PermissionContext)
	if (context === null) {
		throw new Error('usePermissions must be used within a PermissionProvider')
	}
	return context
}

export function useOptionalPermissions() {
	return useContext(PermissionContext)
}
